// SkeletonMiniStatCard.js
import React, { useMemo } from "react";
import { Skeleton, SkeletonCircle, SkeletonPill } from "../../util/skeleton/Skeleton";

/**
 * SkeletonMiniStatCard
 * - Mimics MiniStatCard layout 1:1 (icon box, label/value, optional meta, hint, footer, bottom accent)
 * - Use it when MiniStatCard is loading to avoid layout shift
 */
const SkeletonMiniStatCard = ({
  showMeta = true,
  showHint = true,
  showFooter = false,
  tone = "primary", // to mimic bottom accent tone
}) => {
  const toneMap = useMemo(
    () => ({
      primary: { bd: "rgba(0,123,255,0.18)" },
      success: { bd: "rgba(40,167,69,0.18)" },
      warning: { bd: "rgba(255,193,7,0.20)" },
      danger: { bd: "rgba(220,53,69,0.18)" },
      info: { bd: "rgba(23,162,184,0.18)" },
      secondary: { bd: "rgba(108,117,125,0.18)" },
    }),
    []
  );

  const t = toneMap[tone] || toneMap.primary;

  return (
    <div
      className="card border-0 shadow-sm"
      style={{
        borderRadius: 16,
        overflow: "hidden",
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.92))",
        border: "1px solid rgba(0,0,0,0.04)",
      }}
    >
      <div className="card-body" style={{ padding: "14px 14px 12px" }}>
        {/* Header row */}
        <div className="d-flex align-items-start justify-content-between">
          <div className="d-flex align-items-center">
            {/* Icon container mimic (44x44, radius 14) */}
            <span
              className="d-inline-flex align-items-center justify-content-center mr-3"
              style={{
                width: 44,
                height: 44,
                borderRadius: 14,
                background: "rgba(0,0,0,0.04)",
                border: "1px solid rgba(0,0,0,0.05)",
              }}
            >
              {/* Skeleton icon */}
              <SkeletonCircle size={18} />
            </span>

            {/* Label + value mimic */}
            <div className="pr-2" style={{ minWidth: 160 }}>
              <Skeleton w="110px" h={12} />
              <div style={{ marginTop: 8 }}>
                <Skeleton w="140px" h={16} />
              </div>
            </div>
          </div>

          {/* Meta badge mimic */}
          {showMeta ? (
            <span
              className="badge badge-light"
              style={{
                borderRadius: 999,
                padding: "6px 10px",
                border: "1px solid rgba(0,0,0,0.06)",
                fontWeight: 600,
              }}
            >
              <SkeletonPill w={54} h={14} />
            </span>
          ) : null}
        </div>

        {/* Hint mimic */}
        {showHint ? (
          <div className="mt-2">
            <Skeleton w="85%" h={12} />
            {/* <div style={{ marginTop: 6 }}>
              <Skeleton w="60%" h={12} />
            </div> */}
          </div>
        ) : null}

        {/* Footer mimic */}
        {showFooter ? (
          <div className="mt-2 d-flex align-items-center justify-content-between">
            <SkeletonPill w={72} h={16} />
            <SkeletonPill w={52} h={16} />
          </div>
        ) : null}
      </div>

      {/* Bottom accent mimic */}
      <div style={{ height: 3, background: t.bd }} />
    </div>
  );
};

export default SkeletonMiniStatCard;
